/**
 * Service for handling the assignment of claims to limitations and inserting them into the spec document.
 */
// Unused but kept for future reference
// import { backendService } from './backendService.js';

export class ClaimAssignmentService {
    /**
     * Insert categorized claims under the Detailed Description heading.
     *
     * @param {Object} editor - The Tiptap editor instance
     * @param {Object} claimToLimitationMap - The mapping of claims to their best matching limitations
     * @param {Array} limitations - The list of limitations
     * @param {Object} claims - The map of claim numbers to claim texts
     * @param {Object} literalSupport - Optional map of claim numbers to literal support text
     * @param {Array} claimGroups - Optional array of claim groups for paragraphing
     * @returns {boolean} True if insertion was successful, false if no Detailed Description found
     */
    async insertAssignedClaims(editor, claimToLimitationMap, limitations, _claims, literalSupport = null, claimGroups = null) {
        if (!editor || !claimToLimitationMap || limitations.length === 0) return false;

        try {
            console.log('ClaimAssignmentService: Inserting assigned claims');

            // Find the detailed description section
            const detailedDescriptionInfo = this.findDetailedDescriptionInfo(editor);
            if (!detailedDescriptionInfo.found) {
                console.error('ClaimAssignmentService: Detailed Description section not found');
                return false;
            }

            // Get the current cursor position
            const { selection } = editor.state;
            const cursorPos = selection.anchor;

            // Check if cursor is within the Detailed Description section
            const isInDetailedDescription =
                cursorPos > detailedDescriptionInfo.headingEndPos &&
                (detailedDescriptionInfo.sectionEndPos === -1 || cursorPos < detailedDescriptionInfo.sectionEndPos);

            // Check if claimToLimitationMap has detailed_description property
            // (which would be the case if it came from the backend in debug mode)
            let formattedContent;
            if (claimToLimitationMap.detailed_description) {
                console.log('ClaimAssignmentService: Using detailed description from assign-claims endpoint');
                // We got the detailed description directly from the assignment endpoint
                
                // First, normalize all newlines to \n (handles Windows CRLF)
                const normalizedContent = claimToLimitationMap.detailed_description.replace(/\r\n/g, '\n');
                
                // If we have claimGroups, use them to group sentences into paragraphs
                if (claimGroups && claimGroups.length > 0) {
                    console.log('ClaimAssignmentService: Using claim groups to organize sentences into paragraphs', claimGroups);
                    
                    // Split content into sentences/lines - each line corresponds to a claim's literal support
                    const sentences = normalizedContent.split(/\n/).map(s => s.trim()).filter(s => s.length > 0);
                    console.log(`ClaimAssignmentService: Found ${sentences.length} sentences to group`);
                    
                    // Group sentences based on claimGroups
                    // Each claim group should become one paragraph
                    const paragraphs = [];
                    let sentenceIndex = 0;
                    
                    for (const claimGroup of claimGroups) {
                        // For each claim group, collect the sentences that belong to those claims
                        const groupSentences = [];
                        
                        for (const _claimNum of claimGroup) {
                            // Each claim should have at least one sentence
                            if (sentenceIndex < sentences.length) {
                                groupSentences.push(sentences[sentenceIndex]);
                                sentenceIndex++;
                            }
                        }
                        
                        // Join sentences in this group into a single paragraph
                        if (groupSentences.length > 0) {
                            const paragraphText = groupSentences.join(' ');
                            paragraphs.push(paragraphText);
                        }
                    }
                    
                    // Add any remaining sentences as individual paragraphs
                    while (sentenceIndex < sentences.length) {
                        paragraphs.push(sentences[sentenceIndex]);
                        sentenceIndex++;
                    }
                    
                    console.log(`ClaimAssignmentService: Grouped ${sentences.length} sentences into ${paragraphs.length} paragraphs based on claim groups`);
                    
                    // Format as HTML paragraphs
                    formattedContent = paragraphs
                        .map(p => `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="claim-assignment">${p}</p>`)
                        .join('');
                } else {
                    console.log('ClaimAssignmentService: No claim groups provided, using line-by-line paragraphs');
                    // Fallback to original behavior if no claim groups
                    let paragraphs = normalizedContent.split(/\n/);
                    
                    console.log(`ClaimAssignmentService: Split into ${paragraphs.length} paragraphs`);
                    
                    // Process each paragraph
                    formattedContent = paragraphs
                        .map(p => p.trim())
                        .filter(p => p.length > 0)
                        .map(p => {
                            return `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="claim-assignment">${p}</p>`;
                        })
                        .join('');
                }
                
                console.log('ClaimAssignmentService: Created paragraphs with claim-assignment-paragraph class');
            } else {
                console.log('ClaimAssignmentService: Using locally generated content');
                // Fallback to local formatting if backend doesn't return content
                formattedContent = this.formatAssignedContent(claimToLimitationMap, limitations, literalSupport, claimGroups);
            }

            // Convert formatted content to paragraph nodes
            console.log('ClaimAssignmentService: Converting formatted content to paragraph nodes');
            
            let paragraphNodes;
            if (formattedContent.includes('<p')) {
                // If we have HTML content (from frontend formatting), parse it
                console.log('ClaimAssignmentService: Parsing HTML formatted content');
                paragraphNodes = this.parseHTMLToParagraphs(formattedContent);
            } else {
                // If we have plain text (from backend), split on double newlines
                console.log('ClaimAssignmentService: Parsing plain text formatted content');
                paragraphNodes = this.parseTextToParagraphs(formattedContent);
            }
            
            console.log(`ClaimAssignmentService: Created ${paragraphNodes.length} paragraph nodes`);
            
            // Insert each paragraph as a separate node
            const insertPos = isInDetailedDescription ? cursorPos : detailedDescriptionInfo.headingEndPos;
            console.log(`ClaimAssignmentService: Inserting ${paragraphNodes.length} paragraphs at position ${insertPos}`);
            
            this.insertParagraphNodes(editor, insertPos, paragraphNodes);

            console.log('ClaimAssignmentService: Inserted assigned claims successfully');
            return true;

        } catch (error) {
            console.error('ClaimAssignmentService: Error inserting assigned claims:', error);
            return false;
        }
    }
    
    /**
     * Find information about the Detailed Description section
     *
     * @param {Object} editor - The Tiptap editor instance
     * @returns {Object} Object containing section information:
     *   - found: Boolean indicating if the section was found
     *   - headingPos: Position of the heading node
     *   - headingEndPos: Position after the heading node (where content starts)
     *   - sectionEndPos: Position where the section ends (-1 if it's the last section)
     */
    findDetailedDescriptionInfo(editor) {
        const { state } = editor;
        const { doc } = state;

        // Default result structure
        const result = {
            found: false,
            headingPos: -1,
            headingEndPos: -1,
            sectionEndPos: -1
        };

        // Find the Detailed Description heading
        let foundDetailed = false;

        // Visit all nodes in the document to find headings
        doc.descendants((node, pos) => {
            // If we already found the detailed section and this is another heading,
            // it marks the end of our section
            if (foundDetailed && node.type.name === 'heading') {
                result.sectionEndPos = pos;
                return false; // Stop traversal once we find the next heading
            }

            // Check if this is the detailed description heading
            if (!foundDetailed &&
                node.type.name === 'heading' &&
                node.attrs.sectionType === 'detailed') {

                foundDetailed = true;
                result.found = true;
                result.headingPos = pos;
                // Calculate position right after the heading
                result.headingEndPos = pos + node.nodeSize;
            }
        });

        return result;
    }

    
    /**
     * Format the assigned claims content for insertion
     *
     * @param {Object} claimToLimitationMap - The mapping of claims to their best matching limitations
     * @param {Array} limitations - The list of limitations
     * @param {Object} literalSupport - Optional map of claim numbers to literal support text
     * @param {Array} claimGroups - Optional array of claim groups for paragraphing
     * @returns {string} The formatted HTML content
     */
    formatAssignedContent(claimToLimitationMap, limitations, literalSupport = null, claimGroups = null) {
        let content = ``;

        // Initialize all limitations first
        const limitationGroups = {};
        limitations.forEach((limitation, idx) => {
            limitationGroups[idx] = {
                limitation_text: limitation,
                limitation_idx: idx,
                claims: []
            };
        });

        // Track unassigned claims (score = 0 or no top_element)
        const unassignedClaims = [];

        // Process the claim-to-limitation mapping
        Object.entries(claimToLimitationMap).forEach(([claimNum, claimData]) => {
            // Skip the detailed_description property if it exists
            if (claimNum === 'detailed_description') return;
            
            const topElement = claimData.top_element;
            if (!topElement) return;

            const limitationIdx = topElement.element_idx;
            const score = topElement.score || 0;

            // If score is 0, add to unassigned
            if (score === 0) {
                unassignedClaims.push({
                    claim_num: claimNum,
                    claim_text: claimData.claim_text,
                    score: score
                });
            } else {
                // Add claim to the appropriate limitation group
                if (limitationGroups[limitationIdx]) {
                    limitationGroups[limitationIdx].claims.push({
                        claim_num: claimNum,
                        claim_text: claimData.claim_text,
                        score: score
                    });
                }
            }
        });

        // Sort limitation groups by limitation index
        const sortedLimitationGroups = Object.values(limitationGroups)
            .sort((a, b) => a.limitation_idx - b.limitation_idx);

        // Check if we should group claims or keep them separate
        // For detailed_description context, each claim group has only one claim
        const isDetailedDescription = claimGroups && claimGroups.length > 0 && 
                                      claimGroups.every(group => group.length === 1);
        
        if (claimGroups && claimGroups.length > 0 && !isDetailedDescription) {
            // Only group claims if we have multi-claim groups (brief_summary mode)
            console.log('ClaimAssignmentService: Using claim groups for paragraphing (brief_summary mode)', claimGroups);
            
            // Format content by limitation groups
            sortedLimitationGroups.forEach(group => {
                // Add limitation heading
                const limitationText = group.limitation_text.replace(/\s+/g, ' ').trim();
                content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="claim-assignment">Limitation ${group.limitation_idx + 1}: ${limitationText}</p>`;

                // Sort claims by number
                const sortedClaims = group.claims.sort((a, b) =>
                    parseInt(a.claim_num) - parseInt(b.claim_num)
                );

                // Group claims according to claimGroups
                let processedClaims = new Set();
                
                // Process each claim group
                claimGroups.forEach(claimGroup => {
                    // Find claims in this limitation that belong to the current claim group
                    const claimsInThisGroup = sortedClaims.filter(claim => {
                        const claimNum = parseInt(claim.claim_num);
                        return claimGroup.includes(claimNum) && !processedClaims.has(claim.claim_num);
                    });

                    if (claimsInThisGroup.length > 0) {
                        // Start a paragraph for this claim group
                        let paragraphContent = '';
                        
                        claimsInThisGroup.forEach((claim, index) => {
                            // Get literal support or original claim text
                            let claimContent = literalSupport && literalSupport[claim.claim_num] 
                                ? `Claim ${claim.claim_num}: ${literalSupport[claim.claim_num]}`
                                : `Claim ${claim.claim_num}: ${claim.claim_text}`;
                            
                            // Normalize whitespace
                            claimContent = claimContent.replace(/\s+/g, ' ').trim();
                            
                            // Add claim content with appropriate spacing
                            if (index > 0) {
                                paragraphContent += ' '; // Single space between claims in same group
                            }
                            paragraphContent += claimContent;
                            
                            processedClaims.add(claim.claim_num);
                        });
                        
                        // Add the paragraph for this claim group
                        content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="claim-assignment">${paragraphContent}</p>`;
                    }
                });

                // Process any remaining claims that weren't in any group (fallback)
                const remainingClaims = sortedClaims.filter(claim => !processedClaims.has(claim.claim_num));
                remainingClaims.forEach(claim => {
                    let claimContent = literalSupport && literalSupport[claim.claim_num] 
                        ? `Claim ${claim.claim_num}: ${literalSupport[claim.claim_num]}`
                        : `Claim ${claim.claim_num}: ${claim.claim_text}`;
                    
                    claimContent = claimContent.replace(/\s+/g, ' ').trim();
                    content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="claim-assignment">${claimContent}</p>`;
                });

                // Add a blank paragraph between limitation groups
                content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="claim-assignment"></p>`;
            });
        } else {
            // Use individual paragraphs for each claim (detailed_description mode or no groups)
            console.log('ClaimAssignmentService: Using individual claim paragraphs (detailed_description mode or no groups)');
            
            // Format content by limitation groups using "Detailed Description Skeleton" language
            sortedLimitationGroups.forEach(group => {
                // Add limitation heading with "Detailed Description Skeleton" prefix
                // Normalize whitespace in limitation text and capitalize first character
                const limitationText = group.limitation_text.replace(/\s+/g, ' ').trim();
                const capitalizedLimitationText = limitationText.charAt(0).toUpperCase() + limitationText.slice(1);
                content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="detailed-description-skeleton">[[Detailed Description Skeleton - Limitation ${group.limitation_idx + 1}:]] ${capitalizedLimitationText}</p>`;

                // Sort claims by number
                const sortedClaims = group.claims.sort((a, b) =>
                    parseInt(a.claim_num) - parseInt(b.claim_num)
                );

                // Add each claim in this group with no extra newlines
                sortedClaims.forEach(claim => {
                    // Use literal support if available, otherwise use original claim text
                    let claimContent = literalSupport && literalSupport[claim.claim_num] 
                        ? `[[Based on claim ${claim.claim_num}:]] ${literalSupport[claim.claim_num]}`
                        : `[[Error in generating description for claim ${claim.claim_num}:]]`;
                    
                    // Normalize whitespace - replace multiple spaces, tabs, etc. with single space
                    claimContent = claimContent.replace(/\s+/g, ' ').trim();
                        
                    content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="detailed-description-skeleton">${claimContent}</p>`;
                });

                // Add a single blank paragraph between limitation groups for better spacing
                content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="detailed-description-skeleton"></p>`;
            });
        }

        // Add unassigned claims section if any exist
        if (unassignedClaims.length > 0) {
            // Determine block type based on context
            const blockType = (claimGroups && claimGroups.length > 0 && 
                             !claimGroups.every(group => group.length === 1)) 
                             ? 'claim-assignment' : 'detailed-description-skeleton';
            
            // Add unassigned heading
            content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="${blockType}">[[Unassigned:]]</p>`;

            // Sort unassigned claims by number
            const sortedUnassignedClaims = unassignedClaims.sort((a, b) =>
                parseInt(a.claim_num) - parseInt(b.claim_num)
            );

            // Add each unassigned claim
            sortedUnassignedClaims.forEach(claim => {
                // Use literal support if available, otherwise show error message
                let claimContent = literalSupport && literalSupport[claim.claim_num] 
                    ? `[[Based on claim ${claim.claim_num}:]] ${literalSupport[claim.claim_num]}`
                    : `[[Error in generating description for claim ${claim.claim_num}:]]`;
                
                // Normalize whitespace - replace multiple spaces, tabs, etc. with single space
                claimContent = claimContent.replace(/\s+/g, ' ').trim();
                    
                content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="${blockType}">${claimContent}</p>`;
            });

            // Add a single blank paragraph after unassigned section for better spacing
            content += `<p class="editor-paragraph claim-assignment-paragraph" data-block-type="${blockType}"></p>`;
        }

        return content;
    }

    /**
     * Parse plain text string into paragraph data for insertion as TipTap nodes
     * 
     * @param {string} textContent - Plain text content with double newline separators
     * @returns {Array} Array of paragraph objects with text and attributes
     */
    parseTextToParagraphs(textContent) {
        // Split on double newlines to get paragraphs
        const paragraphs = textContent.split(/\n\n+/).map(p => p.trim());
        
        const paragraphNodes = [];
        for (const paragraph of paragraphs) {
            // Only include non-empty paragraphs or explicit empty paragraphs (for spacing)
            if (paragraph.length > 0 || paragraph === '') {
                paragraphNodes.push({
                    text: paragraph,
                    blockType: 'detailed-description-skeleton',
                    isEmpty: paragraph.length === 0
                });
            }
        }
        
        return paragraphNodes;
    }

    /**
     * Parse HTML string into paragraph data for insertion as TipTap nodes
     * 
     * @param {string} htmlContent - HTML content with <p> tags
     * @returns {Array} Array of paragraph objects with text and attributes
     */
    parseHTMLToParagraphs(htmlContent) {
        // Create a temporary DOM element to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        
        const paragraphs = [];
        const pElements = tempDiv.querySelectorAll('p');
        
        pElements.forEach(pElement => {
            const text = pElement.textContent || pElement.innerText || '';
            const blockType = pElement.getAttribute('data-block-type') || 'detailed-description-skeleton';
            
            // Only include non-empty paragraphs or explicit empty paragraphs (for spacing)
            if (text.trim().length > 0 || pElement.innerHTML.trim() === '') {
                paragraphs.push({
                    text: text.trim(),
                    blockType: blockType,
                    isEmpty: text.trim().length === 0
                });
            }
        });
        
        return paragraphs;
    }

    /**
     * Insert paragraph data as proper TipTap nodes into the editor
     * 
     * @param {Object} editor - TipTap editor instance
     * @param {number} insertPos - Position to insert paragraphs
     * @param {Array} paragraphs - Array of paragraph objects
     */
    insertParagraphNodes(editor, insertPos, paragraphs) {
        const { state } = editor;
        const { tr } = state;
        
        let currentPos = insertPos;
        
        // Create and insert each paragraph as a separate node
        paragraphs.forEach((paragraph, index) => {
            console.log(`ClaimAssignmentService: Inserting paragraph ${index + 1}: "${paragraph.text.substring(0, 50)}..."`);
            
            // Create paragraph node with appropriate attributes
            const paragraphNode = editor.schema.nodes.paragraph.create(
                {
                    blockType: paragraph.blockType,
                    context: 'detailed-description'
                },
                paragraph.isEmpty ? null : editor.schema.text(paragraph.text)
            );
            
            // Insert the node
            tr.insert(currentPos, paragraphNode);
            currentPos += paragraphNode.nodeSize;
        });
        
        // Dispatch the transaction
        editor.view.dispatch(tr);
        console.log(`ClaimAssignmentService: Successfully inserted ${paragraphs.length} paragraph nodes`);
    }
}

export const claimAssignmentService = new ClaimAssignmentService();